/**
 * Queue — triage what came in.
 *
 * The queue is "active items with no triage assessment yet" — an honest
 * definition that doesn't depend on lifecycle-stage bookkeeping. Decisions go
 * through the canonical `submitTriageAssessment`; the store validates that
 * defer/reject carry a rationale, so the UI asks for one before enabling it.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, PauseCircle, X } from 'lucide-react';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useTriageStore } from '@/stores/prioritization/triageStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import {
  createEmptyQuickChecks,
  type TriageOutcome,
  type TriageQuickChecks,
} from '@/types/prioritization/triage';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  PrimaryButton,
  SecondaryButton,
  StatusChip,
} from '../components/primitives';

/**
 * Recording a triage outcome is `items.triage.approve` (canon 06, Triage
 * sub-permissions): the row rules on triage_assessments (migration 953) take
 * ITEM_TRIAGE_APPROVE or ITEM_CRUD, which that action maps to. Build contract
 * §2.8: the decision is drawn only for someone who may record it.
 */
const TRIAGE_LINE =
  'Sorting what came in — pursue, set aside, turn down — takes the Approve Triage Outcome permission (items.triage.approve). A workspace admin can grant it.';

const CHECKS: Array<{ key: keyof TriageQuickChecks; label: string }> = [
  { key: 'inDomain', label: 'Ours to act on' },
  { key: 'understandable', label: 'We understand it' },
  { key: 'actionable', label: 'Something we could do' },
  { key: 'notDuplicate', label: 'Not a duplicate' },
  { key: 'hasSubmitterContext', label: 'We know who/why' },
];

export default function IntakeQueueScreen() {
  const app = getAppDefinition('intake')!;
  const navigate = useNavigate();

  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const assessments = useTriageStore((s) => s.assessments);
  const fetchTriage = useTriageStore((s) => s.fetchFromSupabase);
  const submitTriageAssessment = useTriageStore((s) => s.submitTriageAssessment);
  const can = useUserStore((s) => s.can);
  const canTriage = can('items.triage.approve');

  const [openId, setOpenId] = useState<string | null>(null);
  const [checks, setChecks] = useState<TriageQuickChecks>(createEmptyQuickChecks());
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchItems();
    void fetchTriage();
  }, [fetchItems, fetchTriage]);

  // The store keys assessments by itemId, so its keys ARE the triaged set.
  const triagedIds = useMemo(() => new Set(Object.keys(assessments ?? {})), [assessments]);

  const queue = useMemo(
    () =>
      items
        .filter((i) => (!i.exit_status || i.exit_status === 'active') && !triagedIds.has(i.id))
        .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()),
    [items, triagedIds],
  );

  const openItem = (id: string) => {
    setOpenId(openId === id ? null : id);
    setChecks(createEmptyQuickChecks());
    setRationale('');
    setError(null);
  };

  const decide = async (itemId: string, outcome: TriageOutcome) => {
    if ((outcome === 'defer' || outcome === 'reject') && !rationale.trim()) {
      setError('A short reason is required to set something aside or turn it down.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await submitTriageAssessment(itemId, {
        itemId,
        quickChecks: checks,
        outcome,
        ...(rationale.trim() ? { rationale: rationale.trim() } : {}),
      } as Parameters<typeof submitTriageAssessment>[1]);
      setOpenId(null);
      setRationale('');
      setChecks(createEmptyQuickChecks());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That decision could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell
      app={app}
      backTo="/apps/intake"
      backLabel="Back to drop"
      actions={
        <SecondaryButton onClick={() => navigate('/apps/intake/mine')} className="!px-3 !py-1.5">
          Mine
        </SecondaryButton>
      }
    >
      <ScreenHeading
        title="The queue"
        subtitle="What came in and hasn't been sorted yet. Decide, and the submitter can see what happened."
      />
      <BallLine
        ballWith={
          queue.length > 0
            ? `${canTriage ? 'you' : 'a triager'} — sort what came in`
            : 'nobody — the queue is clear'
        }
      />

      {queue.length === 0 ? (
        <EmptyState
          title="Queue is clear"
          hint="Everything submitted has been sorted. New drops land here."
          action={<PrimaryButton onClick={() => navigate('/apps/intake')}>Drop an idea</PrimaryButton>}
        />
      ) : (
        <div className="space-y-3">
          {queue.map((item) => {
            const open = openId === item.id;
            return (
              <AppCard key={item.id} className="!p-0">
                <button
                  type="button"
                  onClick={() => openItem(item.id)}
                  className="flex w-full items-start gap-3 px-5 py-4 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                      {item.title}
                    </p>
                    {item.description ? (
                      <p className="mt-0.5 truncate font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                        {item.description}
                      </p>
                    ) : null}
                  </div>
                  {item.itemCategory ? (
                    <StatusChip label={item.itemCategory} tone="slate" />
                  ) : (
                    <StatusChip label="Unsorted" tone="neutral" />
                  )}
                </button>

                {open ? (
                  canTriage ? (
                  <div className="border-t border-gray-100 px-5 py-4 dark:border-[var(--rule)]">
                    <Eyebrow>Quick checks (optional)</Eyebrow>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {CHECKS.map((c) => (
                        <button
                          key={c.key}
                          type="button"
                          onClick={() => setChecks({ ...checks, [c.key]: !checks[c.key] })}
                          className={`rounded-md border px-2.5 py-1 font-body text-xs ${
                            checks[c.key]
                              ? 'border-alpine-green-500 bg-alpine-green-50 text-alpine-green-700 dark:bg-alpine-green-900/30 dark:text-alpine-green-300'
                              : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
                          }`}
                        >
                          {checks[c.key] ? '✓ ' : ''}
                          {c.label}
                        </button>
                      ))}
                    </div>

                    <label
                      className="mt-4 block font-body text-xs text-gray-500 dark:text-[var(--text-ter)]"
                      htmlFor={`why-${item.id}`}
                    >
                      Why? (required to set aside or turn down)
                    </label>
                    <input
                      id={`why-${item.id}`}
                      value={rationale}
                      onChange={(e) => setRationale(e.target.value)}
                      placeholder="One line the submitter will understand"
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
                    />

                    {error ? (
                      <p className="mt-2 font-body text-xs text-luminous-coral-700 dark:text-luminous-coral-300">
                        {error}
                      </p>
                    ) : null}

                    <div className="mt-4 flex flex-wrap gap-2">
                      <PrimaryButton
                        onClick={() => void decide(item.id, 'pursue')}
                        disabled={busy}
                        className="!px-3 !py-1.5 !text-xs"
                      >
                        <Check className="h-3.5 w-3.5" /> Pursue
                      </PrimaryButton>
                      <SecondaryButton
                        onClick={() => void decide(item.id, 'defer')}
                        disabled={busy}
                        className="!px-3 !py-1.5 !text-xs"
                      >
                        <PauseCircle className="h-3.5 w-3.5" /> Set aside
                      </SecondaryButton>
                      <SecondaryButton
                        onClick={() => void decide(item.id, 'reject')}
                        disabled={busy}
                        className="!px-3 !py-1.5 !text-xs"
                      >
                        <X className="h-3.5 w-3.5" /> Turn down
                      </SecondaryButton>
                    </div>
                  </div>
                  ) : (
                    <p className="border-t border-gray-100 px-5 py-4 font-body text-sm text-gray-500 dark:border-[var(--rule)] dark:text-[var(--text-ter)]">
                      {TRIAGE_LINE}
                    </p>
                  )
                ) : null}
              </AppCard>
            );
          })}
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <SecondaryButton onClick={() => navigate('/apps/items')}>
          Open the Items app <ArrowRight className="h-4 w-4" />
        </SecondaryButton>
      </div>
    </AppShell>
  );
}
